import React, { useState, useEffect } from 'react';
import { Box, Button, Chip, Collapse, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { countries } from '../../utils/countriesList';
import { IShippingAddress } from '../../redux/buzzbridge/types';

interface IShippingAddressProps {
    value?: IShippingAddress;
    onChange: (address: IShippingAddress) => void;
}

const ShippingAddress: React.FC<IShippingAddressProps> = ({ value, onChange }) => {
    const [expanded, setExpanded] = useState(false);
    const [address, setAddress] = useState<IShippingAddress>({
        addressLine1: '',
        addressLine2: '',
        country: '',
        zipcode: '',
    });

    const { themeStyle } = useThemeContext();

    useEffect(() => {
        if (value && typeof value === 'object' && 'addressLine1' in value) {
            setAddress(value);
        }
    }, [value]);

    const handleChange = (field: string, val: string) => {
        const updated = { ...address, [field]: val };
        setAddress(updated);
        onChange?.(updated);
    };

    const formattedAddress =
        `${address.addressLine1}${address.addressLine2 ? ', ' + address.addressLine2 : ''}, ` +
        `${countries.find(c => c.code === address.country)?.name || address.country} - ${address.zipcode}`;

    const commonTextFieldProps = {
        sx: {
            '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '& fieldset': {
                    borderColor: theme[themeStyle].form_divider,
                    borderWidth: '1px',
                },
                '&:hover fieldset': {
                    borderColor: theme[themeStyle].highlight,
                    borderWidth: '1px',
                },
                '&.Mui-focused fieldset': {
                    borderColor: theme[themeStyle].highlight,
                    borderWidth: '2px',
                },
            },
            '& .MuiInputLabel-root': {
                color: theme[themeStyle].form_label,
                fontFamily: FONT_ENUM.MAIN_MEDIUM,
            },
            '& .MuiInputLabel-root.Mui-focused': { color: theme[themeStyle].highlight },
            '& .MuiInputBase-input': {
                color: theme[themeStyle].primary_white,
                fontFamily: FONT_ENUM.MAIN_MEDIUM,
            },
            '& ::placeholder': {
                color: theme[themeStyle].placeholderGray,
                fontFamily: FONT_ENUM.REGULAR_HEADER,
            },
        },
    };

    const commonSelectProps = {
        sx: {
            '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '& fieldset': {
                    borderColor: theme[themeStyle].form_divider,
                    borderWidth: '1px',
                },
                '&:hover fieldset': {
                    borderColor: theme[themeStyle].highlight,
                    borderWidth: '1px',
                },
                '&.Mui-focused fieldset': {
                    borderColor: theme[themeStyle].highlight,
                    borderWidth: '2px',
                },
            },
            '& .MuiSelect-select': {
                color: theme[themeStyle].primary_white,
                fontFamily: FONT_ENUM.MAIN_MEDIUM,
            },
            '& .MuiSvgIcon-root': {
                color: theme[themeStyle].primary_white,
            },
        },
    };

    return (
        <Box
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{
                p: 2,
                border: `1px solid ${theme[themeStyle].form_divider}`,
                borderRadius: '8px',
                backgroundColor: theme[themeStyle].secondaryBg,
            }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Chip
                    label={formattedAddress}
                    onClick={() => setExpanded(prev => !prev)}
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 13,
                        color: theme[themeStyle].tag_white,
                        backgroundColor: theme[themeStyle].secondaryBg,
                        '& .MuiChip-deleteIcon': { color: theme[themeStyle].tag_white },
                    }}
                />
                <Button
                    size="small"
                    onClick={() => setExpanded(prev => !prev)}
                    sx={{
                        color: theme[themeStyle].highlight,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        textTransform: 'none',
                        '&:hover': {
                            backgroundColor: theme[themeStyle].form_divider,
                        },
                    }}>
                    {expanded ? 'Collapse' : 'Edit'}
                </Button>
            </Box>
            <Collapse in={expanded}>
                <Box display="flex" flexDirection="column" gap={2}>
                    <TextField
                        label="Address Line 1"
                        value={address.addressLine1}
                        onChange={e => handleChange('addressLine1', e.target.value)}
                        required
                        {...commonTextFieldProps}
                    />
                    <TextField
                        label="Address Line 2"
                        value={address.addressLine2}
                        onChange={e => handleChange('addressLine2', e.target.value)}
                        {...commonTextFieldProps}
                    />
                    <FormControl
                        fullWidth
                        sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme[themeStyle].form_divider,
                            },
                            '&:hover fieldset': { borderColor: theme[themeStyle].highlight },
                        }}>
                        <InputLabel
                            sx={{
                                color: theme[themeStyle].form_label,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                '&.Mui-focused': { color: theme[themeStyle].highlight },
                            }}>
                            Country
                        </InputLabel>
                        <Select
                            value={address.country}
                            onChange={e => handleChange('country', e.target.value)}
                            label="Country"
                            {...commonSelectProps}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        backgroundColor: theme[themeStyle].dropdown_black,
                                        border: `1px solid ${theme[themeStyle].form_divider}`,
                                        borderRadius: '8px',
                                        '& .MuiMenuItem-root': {
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '&:hover': {
                                                backgroundColor: theme[themeStyle].form_divider,
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: theme[themeStyle].highlight,
                                                color: theme[themeStyle].primary_white,
                                                '&:hover': {
                                                    backgroundColor: theme[themeStyle].highlight,
                                                },
                                            },
                                        },
                                    },
                                },
                            }}>
                            <MenuItem value="">
                                <em>Select Country</em>
                            </MenuItem>
                            {countries.map(({ code, name }) => (
                                <MenuItem key={code} value={code}>
                                    {name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Zip Code"
                        value={address.zipcode}
                        onChange={e => handleChange('zipcode', e.target.value)}
                        required
                        {...commonTextFieldProps}
                    />
                </Box>
            </Collapse>
        </Box>
    );
};

export { ShippingAddress };
